/* DOM-free semantic HTML comparison for the assessment/composition layer.
 *
 * Why this exists: TipTap v3 re-serializes stored HTML on load with node
 * attributes in a different order (e.g. `data-options`/`data-type` on choice
 * buttons), producing a byte-different but semantically-identical string. A
 * raw `===` compare treats that as a real edit — which silently unsigns signed
 * assessments (`Assessment.setNthSectionText` clears the attester on any
 * change). `htmlEquivalent` ignores attribute ordering and quote style so a
 * phantom re-emit is recognized as unchanged.
 *
 * Lives in @seen/core (not @seen/react's DOM-based `areHtmlEqual`) because the
 * guard runs in the model layer, which must stay DOM-free for Node/Lambda and
 * script use.
 *
 * Conservative by construction: it only normalizes attribute order and quote
 * style. Any other difference (text, element add/remove/reorder, attribute
 * name or value change, whitespace in text) leaves the strings unequal, so a
 * real edit is never mistaken for a phantom no-op. */

const START_TAG =
  /<([a-zA-Z][a-zA-Z0-9-]*)((?:\s+[^\s/>"'=]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s/>]+))?)*)\s*(\/?)>/g
const ATTR = /([^\s/>"'=]+)(?:\s*=\s*("[^"]*"|'[^']*'|[^\s/>]+))?/g

function unquote(value: string): string {
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    return value.slice(1, -1)
  }
  return value
}

/* Rewrite every start tag with its attributes sorted by name and re-quoted in a
 * canonical `name="value"` form. End tags, text, and whitespace are untouched. */
function canonicalizeAttributeOrder(html: string): string {
  return html.replace(START_TAG, (_match, tagName, attrsStr, selfClose) => {
    const attrs: string[] = []
    for (const attr of (attrsStr as string).matchAll(ATTR)) {
      const name = attr[1]
      const rawValue = attr[2]
      attrs.push(
        rawValue === undefined ? name : `${name}="${unquote(rawValue)}"`
      )
    }
    attrs.sort()
    const body = attrs.length > 0 ? ` ${attrs.join(' ')}` : ''
    return `<${tagName}${body}${selfClose ? ' /' : ''}>`
  })
}

/* True when two HTML strings differ only by start-tag attribute ordering and
 * quote style. Returns true for byte-identical input as a fast path. */
export function htmlEquivalent(a: string, b: string): boolean {
  if (a === b) {
    return true
  }
  return canonicalizeAttributeOrder(a) === canonicalizeAttributeOrder(b)
}
